import supportedLanguages from './supportedLanguages';
import { Language, Languages, SupportedLanguage } from './types';

/**
 * This function is to return a new URL with this structure: newPathLanguage + (pathname excluding the locale). Have a look at the unit tests for some examples.
 * @param newPathLanguage The locale in the new URL
 * @param pathname
 * @param base The base URL including the scheme and host
 * @param search The query params
 * @param hash The hash value
 * @param basePath The basePath to insert after the locale (e.g., '/claims')
 * @returns The new URL
 */

export function getNewURLWithLocale(
  newPathLanguage: string,
  pathname: string,
  base: string,
  search?: string,
  hash?: string,
  basePath?: string,
) {
  let newPathname = pathname;

  // Language is the first path segment after the leading slash
  const pathLanguage = newPathname.split('/')[1]?.toLowerCase() as SupportedLanguage;

  // Remove language from the path if it was valid
  if (supportedLanguages[pathLanguage]) {
    newPathname = newPathname.replace(`/${pathLanguage}`, '');
  }

  // If no base path is provided we can assume that no path segment was stripped
  const basePathSegment = basePath || '';

  // Remove the base path from the path if it exists
  if (basePathSegment) {
    newPathname = newPathname.replace(new RegExp(`^${basePathSegment}`), '');
  }

  // Default to no trailing slash when this is the whole path
  const pathSuffix = newPathname === '/' ? '' : newPathname;
  return new URL(`/${newPathLanguage}${basePathSegment}${pathSuffix}${search || ''}${hash || ''}`, base);
}

/**
 * This function is to get the first supported language from the accept-language HTTP header.
 * @param acceptLanguageHeader The accept-language HTTP header
 * @param languages The supported languages
 * @param userCountry
 * @param userContinent
 * @returns The supported language
 */
export function getLanguageFromAcceptLanguageHeader(
  acceptLanguageHeader: string,
  languages: Languages,
  userCountry: string,
  userContinent: string,
) {
  function getSupportedLanguage(language: string) {
    if (language.startsWith('pt') && userCountry === 'BR' && languages['pt-br']) {
      return 'pt-br';
    }
    if (language === 'en' && (userCountry === 'US' || userContinent === 'SA') && languages['en-us']) {
      return 'en-us';
    }

    if (languages[language as Language]) {
      return language;
    }
    return null;
  }

  const acceptLanguageRegex = /((([a-zA-Z]+)(-[a-zA-Z0-9]+){0,2})(;q=([0-1]\.[0-9]+)?)?)/g;
  let m;
  do {
    m = acceptLanguageRegex.exec(acceptLanguageHeader.toLocaleLowerCase());

    let supportedLanguage;
    const languageWithRegion = m?.[2];
    if (languageWithRegion) {
      supportedLanguage = getSupportedLanguage(languageWithRegion);
      if (supportedLanguage) return supportedLanguage;
    }
    const languageWithoutRegion = m?.[3];
    if (languageWithoutRegion) {
      supportedLanguage = getSupportedLanguage(languageWithoutRegion);
      if (supportedLanguage) return supportedLanguage;
    }
  } while (m !== null);
  return 'en';
}
